import React from "react";
import {
    Dialog,
    DialogContent,
    Typography,
    List,
    ListItem,
    ListItemText,
    Button,
    Box,
    IconButton,
    styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
    "& .MuiPaper-root": {
        backgroundColor: "rgb(13, 32, 63)",
        color: "#ffffff",
        borderRadius: "12px",
        maxWidth: "600px",
        width: "100%",
        padding: theme.spacing(2),
        position: "relative",
    },
}));

const StyledButton = styled(Button)(({ variant }) => ({
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.875rem",
    padding: "6px 16px",
    borderRadius: "6px",
    ...(variant === "outlined"
        ? {
              borderColor: "rgba(255, 255, 255, 0.4)",
              color: "#ffffff",
              "&:hover": {
                  borderColor: "#ffffff",
                  backgroundColor: "rgba(255, 255, 255, 0.08)",
              },
          }
        : {
              backgroundColor: "#ffffff",
              color: "rgb(13, 32, 63)",
              "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
              },
          }),
}));

export default function WelcomeModal({ open, onClose, onStartExploring }) {
    const handleStartExploring = () => {
        if (onStartExploring) {
            onStartExploring();
            return;
        }
        onClose?.();
    };
    return (
        <StyledDialog
            open={open}
            onClose={onClose} // Handles Escape key and backdrop clicks
        >
            {/* Top Right 'X' Close Button */}
            <IconButton
                aria-label="close"
                onClick={onClose}
                sx={{
                    position: "absolute",
                    right: 12,
                    top: 12,
                    color: "rgba(255, 255, 255, 0.7)",
                    "&:hover": {
                        color: "#ffffff",
                        backgroundColor: "rgba(255, 255, 255, 0.1)",
                    },
                }}
            >
                <CloseIcon />
            </IconButton>

            <DialogContent sx={{ p: 2 }}>
                {/* Title */}
                <Typography
                    variant="h5"
                    component="h2"
                    sx={{ fontWeight: 700, mb: 2, pr: 4 }}
                >
                    Welcome to Vtrender Free
                </Typography>

                {/* Subtitle */}
                <Typography
                    variant="body1"
                    sx={{ mb: 2, color: "rgba(255, 255, 255, 0.9)" }}
                >
                    Your Free access does not expire.
                </Typography>

                {/* Description */}
                <Typography
                    variant="body2"
                    sx={{
                        mb: 2,
                        color: "rgba(255, 255, 255, 0.8)",
                        lineHeight: 1.6,
                    }}
                >
                    You have access to market charts, options analytics,
                    positioning data and Vtrender's learning resources with
                    15-minute delayed market data. Three resources are always
                    available at the top of your screen:
                </Typography>

                {/* Feature List */}
                <List
                    sx={{
                        listStyleType: "disc",
                        pl: 2.5,
                        mb: 2,
                        "& .MuiListItem-root": {
                            display: "list-item",
                            py: 0.5,
                        },
                    }}
                >
                    <ListItem disablePadding>
                        <ListItemText
                            dense
                            primary={
                                <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                                >
                                    <Box
                                        component="strong"
                                        sx={{
                                            color: "#ffffff",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Tools Walkthrough
                                    </Box>{" "}
                                    — see how each tool works
                                </Typography>
                            }
                        />
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText
                            dense
                            primary={
                                <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                                >
                                    <Box
                                        component="strong"
                                        sx={{
                                            color: "#ffffff",
                                            fontWeight: 700,
                                        }}
                                    >
                                        User Manual
                                    </Box>{" "}
                                    — understand the charts and their settings
                                </Typography>
                            }
                        />
                    </ListItem>
                    <ListItem disablePadding>
                        <ListItemText
                            dense
                            primary={
                                <Typography
                                    variant="body2"
                                    sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                                >
                                    <Box
                                        component="strong"
                                        sx={{
                                            color: "#ffffff",
                                            fontWeight: 700,
                                        }}
                                    >
                                        Glossary
                                    </Box>{" "}
                                    — look up market and Vtrender terminology
                                </Typography>
                            }
                        />
                    </ListItem>
                </List>

                {/* Action Buttons */}
                <Box sx={{ display: "flex", gap: 1.5, mt: 3 }}>
                    <StyledButton variant="contained" onClick={handleStartExploring}>
                        Start exploring
                    </StyledButton>
                    <StyledButton variant="outlined" onClick={onClose}>
                        I'll explore myself
                    </StyledButton>
                </Box>
            </DialogContent>
        </StyledDialog>
    );
}
